import {
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import {
  createAppKit,
  useAppKit,
  useAppKitAccount,
  useAppKitNetwork,
  useAppKitProvider,
} from "@reown/appkit/react";
import { type AppKitNetwork } from "@reown/appkit/networks";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getAddress, isAddress, type EIP1193Provider } from "viem";
import { WagmiProvider } from "wagmi";

import { targetChain } from "@stonkland/protocol/chain";
import { publicEnvironment } from "@stonkland/protocol/public-env";
import {
  WalletSessionContext,
  type WalletSession,
} from "@web/wallet/session";

type AppKitRuntime = {
  adapter: WagmiAdapter;
  network: AppKitNetwork;
};

const projectId = publicEnvironment("VITE_REOWN_PROJECT_ID")?.trim();
const queryClient = new QueryClient();

function applicationOrigin() {
  return typeof window === "undefined"
    ? "https://stonkland.com"
    : window.location.origin;
}

function createRuntime(): AppKitRuntime | undefined {
  if (!projectId) return undefined;

  const network = targetChain as AppKitNetwork;
  const adapter = new WagmiAdapter({
    networks: [network],
    projectId,
    ssr: false,
  });
  const origin = applicationOrigin();
  createAppKit({
    adapters: [adapter],
    networks: [network],
    defaultNetwork: network,
    projectId,
    metadata: {
      name: "Stonkland",
      description: "Portfolio NFTs and RWA emissions on Robinhood Chain.",
      url: origin,
      icons: [`${origin}/brand/stonkland-mark.svg`],
    },
    allowUnsupportedChain: false,
    enableWalletConnect: true,
    features: {
      analytics: false,
      email: false,
      socials: false,
      swaps: false,
      onramp: false,
      send: false,
      history: false,
      connectorTypeOrder: [
        "injected",
        "walletConnect",
        "recent",
        "featured",
        "recommended",
        "external",
        "custom",
      ],
    },
    themeMode: "dark",
    themeVariables: {
      "--w3m-accent": "#c6ff3d",
      "--w3m-color-mix": "#070908",
      "--w3m-color-mix-strength": 30,
      "--w3m-border-radius-master": "2px",
      "--w3m-font-family": "Inter, ui-sans-serif, system-ui, sans-serif",
      "--w3m-z-index": 1000,
    },
  });

  return { adapter, network };
}

const runtime = createRuntime();

const unavailableSession: WalletSession = {
  configured: false,
  status: "disconnected",
  openAccount: async () => undefined,
  openConnect: async () => undefined,
  switchToTargetNetwork: async () => undefined,
};

function ConnectedWalletSession({
  children,
  network,
}: {
  children: ReactNode;
  network: AppKitNetwork;
}) {
  const { open } = useAppKit();
  const { address, isConnected, status } = useAppKitAccount({
    namespace: "eip155",
  });
  const { chainId, switchNetwork } = useAppKitNetwork();
  const { walletProvider } = useAppKitProvider<EIP1193Provider>("eip155");

  const account =
    isConnected && address && isAddress(address) ? getAddress(address) : undefined;
  const numericChainId = Number(chainId);

  const openConnect = useCallback(async () => {
    await open({ view: "Connect", namespace: "eip155" });
  }, [open]);
  const openAccount = useCallback(async () => {
    await open({ view: "Account", namespace: "eip155" });
  }, [open]);
  const switchToTargetNetwork = useCallback(async () => {
    await switchNetwork(network);
  }, [network, switchNetwork]);

  const session = useMemo<WalletSession>(
    () => ({
      account,
      chainId: Number.isFinite(numericChainId) ? numericChainId : undefined,
      configured: true,
      provider: isConnected ? walletProvider : undefined,
      status: status || "disconnected",
      openAccount,
      openConnect,
      switchToTargetNetwork,
    }),
    [
      account,
      isConnected,
      numericChainId,
      openAccount,
      openConnect,
      status,
      switchToTargetNetwork,
      walletProvider,
    ],
  );

  return (
    <WalletSessionContext.Provider value={session}>
      {children}
    </WalletSessionContext.Provider>
  );
}

export function AppKitProvider({ children }: { children: ReactNode }) {
  if (!runtime) {
    return (
      <WalletSessionContext.Provider value={unavailableSession}>
        {children}
      </WalletSessionContext.Provider>
    );
  }

  return (
    <WagmiProvider config={runtime.adapter.wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <ConnectedWalletSession network={runtime.network}>
          {children}
        </ConnectedWalletSession>
      </QueryClientProvider>
    </WagmiProvider>
  );
}
